/**
 * TutorialInputFilter — wraps an InputProvider to gate keys not yet unlocked.
 *
 * The tutorial progressively unlocks keys as the player advances through steps.
 * Keys accumulate — once unlocked, they stay unlocked forever within the tutorial.
 * Menu keys (Escape, arrows for menu nav) are always allowed.
 */

import { Keys } from 'bonkjs';
import type { InputProvider } from '../input';
import type { GameState } from '../types';

/** Keys that are always allowed regardless of tutorial step (menu navigation). */
const ALWAYS_ALLOWED: ReadonlySet<string> = new Set([
  Keys.Escape,
  Keys.ArrowUp,
  Keys.ArrowDown,
  Keys.ArrowLeft,
  Keys.ArrowRight,
  Keys.Space,   // Also used for menu confirm — filtered only during gameplay
  Keys.Enter,
  Keys.W,
  Keys.S,
]);

export class TutorialInputFilter implements InputProvider {
  constructor(
    private readonly base: InputProvider,
    private readonly getState: () => GameState,
  ) {}

  getKey(key: string): boolean {
    const state = this.getState();
    const ms = state.modeState;

    // Not in tutorial mode — pass through
    if (ms.mode !== 'tutorial') return this.base.getKey(key);

    // Menu phases — allow all input (menu navigation)
    const isMenu = state.phase === 'title_screen' || state.phase === 'mode_select'
      || state.phase === 'tank_select' || state.phase === 'stage_select';
    if (isMenu) return this.base.getKey(key);

    // Paused — allow all input (pause menu navigation)
    if (state.paused) return this.base.getKey(key);

    // Tutorial complete — allow all
    if (ms.tutorialComplete) return this.base.getKey(key);

    // Always-allowed keys
    if (ALWAYS_ALLOWED.has(key)) return this.base.getKey(key);

    // Check if key is in unlocked set
    if (ms.tutorialUnlockedKeys.includes(key)) return this.base.getKey(key);

    // Key is locked — return false (blocked)
    return false;
  }
}
